import { useQuery } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { zodParseWithLog } from "src/api/apiErrorLogs";
import { EntityID } from "src/api/apiHelpers.ts";
import { isQueryReady } from "src/api/axiosClient/utils";
import { PostListSchema } from "./types/postList.types";
import { OlapScoresQuery } from "src/context/OlapScores/useOlapScoresParams";

export const postStatus = {
    calculation: 0,
    simulation: 1,
    objective: 2,
} as const;
export type postStatusKeys = keyof typeof postStatus;

type Options = {
    queryKey: string;
    enabled?: boolean;
    params?: AxiosParams;
    scoresQuery?: OlapScoresQuery;
};

export const anyAllSimulationKey = "getAllSimulations" as const;

const useGetAllPosts = (options: Options) => {
    const { queryKey, enabled = true, params = {}, scoresQuery } = options;

    const scoresQueryKey = scoresQuery ? JSON.stringify(scoresQuery) : undefined;

    const keyParams = Object.values(params);
    const query = useQuery({
        cacheTime: 0,
        queryKey: [anyAllSimulationKey, queryKey, ...keyParams, scoresQueryKey],
        enabled: enabled,
        queryFn: async () => {
            const url = `/api/ssq-posts`;
            return loggedInApiCall.get(url, {
                headers: {
                    Accept: "application/json",
                },
                params: {
                    ...params,
                    ...scoresQuery,
                },
            });
        },
        select: (req) => {
            const post = req.data;
            const data = zodParseWithLog(post, PostListSchema.array(), queryKey);
            return data;
        },
    });

    return {
        posts: query.data,
        ...query,
    };
};

export type CompanyPostsOptions = {
    entityId: EntityID;
    status: postStatusKeys;
    scoresQuery?: OlapScoresQuery;
    extraParams?: AxiosParams;
    isEnabled?: boolean;
};
export const useGetCompanyPosts = (options: CompanyPostsOptions) => {
    const { entityId, status, scoresQuery, extraParams = {}, isEnabled = true } = options;

    const _isEnabled = entityId !== undefined && isEnabled;
    const statusNumber = postStatus[status];
    const query = useGetAllPosts({
        queryKey: "company",
        enabled: _isEnabled,
        params: {
            "organization.id": entityId,
            exists: {
                product: false,
                ingredient: false,
            },
            status: statusNumber,
            ...extraParams,
        },
        scoresQuery,
    });

    const isReady = isQueryReady(query, _isEnabled);

    return {
        ...query,
        posts: query.posts,
        isReady,
    };
};
